import React, { useState, useEffect, useCallback } from 'react';
import {
  ExportFormat,
  ExportScope,
  PDFExportOptions,
  JPGExportOptions,
  PaperSize,
  PaperOrientation,
  ImageQuality,
  ImageScale,
  ExportProgress,
  DEFAULT_PDF_OPTIONS,
  DEFAULT_JPG_OPTIONS,
} from '../types/Export';
import { generateDefaultFilename } from '../utils/exportUtils';
import { useI18n } from '../contexts/I18nContext';
import '../styles/components/ExportModal.css';

interface ExportModalProps {
  isOpen: boolean;
  onClose: () => void;
  onExport: (options: PDFExportOptions | JPGExportOptions) => Promise<void>;
  hasSelection: boolean;
  progress: ExportProgress | null;
}

const ExportModal: React.FC<ExportModalProps> = ({
  isOpen,
  onClose,
  onExport,
  hasSelection,
  progress,
}) => {
  const { t } = useI18n();

  // 포맷 선택
  const [format, setFormat] = useState<ExportFormat>('pdf');

  // 공통 옵션
  const [scope, setScope] = useState<ExportScope>('all');
  const [filename, setFilename] = useState<string>(generateDefaultFilename());

  // PDF 옵션
  const [paperSize, setPaperSize] = useState<PaperSize>(DEFAULT_PDF_OPTIONS.paperSize);
  const [orientation, setOrientation] = useState<PaperOrientation>(DEFAULT_PDF_OPTIONS.orientation);
  const [margin, setMargin] = useState<number>(DEFAULT_PDF_OPTIONS.margin);

  // JPG 옵션
  const [quality, setQuality] = useState<ImageQuality>(DEFAULT_JPG_OPTIONS.quality);
  const [scale, setScale] = useState<ImageScale>(DEFAULT_JPG_OPTIONS.scale);
  const [backgroundColor, setBackgroundColor] = useState<string>(DEFAULT_JPG_OPTIONS.backgroundColor);

  // 내보내기 중 상태
  const [isExporting, setIsExporting] = useState(false);

  // 모달이 열릴 때 파일명 초기화
  useEffect(() => {
    if (isOpen) {
      setFilename(generateDefaultFilename());
    }
  }, [isOpen]);

  // 선택된 객체가 없으면 selection 범위 비활성화
  useEffect(() => {
    if (!hasSelection && scope === 'selection') {
      setScope('all');
    }
  }, [hasSelection, scope]);

  // 진행 상태 변경 시 내보내기 상태 업데이트
  useEffect(() => {
    if (progress?.status === 'complete' || progress?.status === 'error') {
      setIsExporting(false);
      if (progress?.status === 'complete') {
        // 성공 시 잠시 후 모달 닫기
        setTimeout(() => {
          onClose();
        }, 1000);
      }
    }
  }, [progress, onClose]);

  const handleExport = useCallback(async () => {
    setIsExporting(true);

    const baseOptions = {
      scope,
      filename,
    };

    try {
      if (format === 'pdf') {
        const pdfOptions: PDFExportOptions = {
          ...baseOptions,
          format: 'pdf',
          paperSize,
          orientation,
          margin,
        };
        await onExport(pdfOptions);
      } else {
        const jpgOptions: JPGExportOptions = {
          ...baseOptions,
          format: 'jpg',
          quality,
          scale,
          backgroundColor,
        };
        await onExport(jpgOptions);
      }
    } catch (error) {
      setIsExporting(false);
    }
  }, [format, scope, filename, paperSize, orientation, margin, quality, scale, backgroundColor, onExport]);

  const handleKeyDown = useCallback((e: React.KeyboardEvent) => {
    if (e.key === 'Escape' && !isExporting) {
      onClose();
    } else if ((e.ctrlKey || e.metaKey) && e.key === 'Enter' && !isExporting) {
      e.preventDefault();
      handleExport();
    }
  }, [isExporting, onClose, handleExport]);

  if (!isOpen) return null;

  return (
    <div className="export-modal-overlay" onClick={isExporting ? undefined : onClose}>
      <div
        className="export-modal-content"
        onClick={(e) => e.stopPropagation()}
        onKeyDown={handleKeyDown}
        tabIndex={0}
      >
        {/* 헤더 */}
        <div className="export-modal-header">
          <h2 className="export-modal-title">{t.modals.export.title}</h2>
          {!isExporting && (
            <button className="export-modal-close" onClick={onClose} title={t.common.close}>
              <svg width="20" height="20" viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M15 5L5 15M5 5l10 10" />
              </svg>
            </button>
          )}
        </div>

        {/* 본문 */}
        <div className="export-modal-body">
          {/* 포맷 선택 */}
          <div className="export-section">
            <label className="export-section-label">{t.modals.export.format}</label>
            <div className="export-format-tabs">
              <button
                className={`export-format-tab ${format === 'pdf' ? 'active' : ''}`}
                onClick={() => setFormat('pdf')}
                disabled={isExporting}
              >
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                  <path d="M14 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V8z" />
                  <polyline points="14,2 14,8 20,8" />
                  <path d="M9 15h6M9 11h6" />
                </svg>
                <span>PDF</span>
              </button>
              <button
                className={`export-format-tab ${format === 'jpg' ? 'active' : ''}`}
                onClick={() => setFormat('jpg')}
                disabled={isExporting}
              >
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                  <rect x="3" y="3" width="18" height="18" rx="2" ry="2" />
                  <circle cx="8.5" cy="8.5" r="1.5" />
                  <polyline points="21,15 16,10 5,21" />
                </svg>
                <span>JPG</span>
              </button>
            </div>
          </div>

          {/* 범위 선택 */}
          <div className="export-section">
            <label className="export-section-label">{t.modals.export.scope}</label>
            <div className="export-scope-options">
              <label className={`export-radio-label ${scope === 'all' ? 'active' : ''}`}>
                <input
                  type="radio"
                  name="scope"
                  value="all"
                  checked={scope === 'all'}
                  onChange={() => setScope('all')}
                  disabled={isExporting}
                />
                <span className="export-radio-text">{t.modals.export.allCanvas}</span>
                <span className="export-radio-desc">{t.modals.export.includeAll}</span>
              </label>
              <label className={`export-radio-label ${scope === 'viewport' ? 'active' : ''}`}>
                <input
                  type="radio"
                  name="scope"
                  value="viewport"
                  checked={scope === 'viewport'}
                  onChange={() => setScope('viewport')}
                  disabled={isExporting}
                />
                <span className="export-radio-text">{t.modals.export.currentView}</span>
                <span className="export-radio-desc">{t.modals.export.visibleOnly}</span>
              </label>
              <label
                className={`export-radio-label ${scope === 'selection' ? 'active' : ''} ${!hasSelection ? 'disabled' : ''}`}
              >
                <input
                  type="radio"
                  name="scope"
                  value="selection"
                  checked={scope === 'selection'}
                  onChange={() => setScope('selection')}
                  disabled={isExporting || !hasSelection}
                />
                <span className="export-radio-text">{t.modals.export.selectedObjects}</span>
                <span className="export-radio-desc">{hasSelection ? t.modals.export.selectionOnly : t.modals.export.selectObjects}</span>
              </label>
            </div>
          </div>

          {/* 파일명 */}
          <div className="export-section">
            <label className="export-section-label" htmlFor="export-filename">{t.modals.export.filename}</label>
            <div className="export-filename-input-wrapper">
              <input
                id="export-filename"
                type="text"
                value={filename}
                onChange={(e) => setFilename(e.target.value)}
                className="export-filename-input"
                placeholder={t.modals.export.enterFilename}
                disabled={isExporting}
              />
              <span className="export-filename-ext">.{format}</span>
            </div>
          </div>

          {/* PDF 전용 옵션 */}
          {format === 'pdf' && (
            <div className="export-options-group">
              <div className="export-section export-section-inline">
                <label className="export-section-label">{t.modals.export.paperSize}</label>
                <select
                  value={paperSize}
                  onChange={(e) => setPaperSize(e.target.value as PaperSize)}
                  className="export-select"
                  disabled={isExporting}
                >
                  <option value="A4">A4</option>
                  <option value="Letter">Letter</option>
                  <option value="fit">{t.modals.export.fitToContent}</option>
                </select>
              </div>

              <div className="export-section export-section-inline">
                <label className="export-section-label">{t.modals.export.orientation}</label>
                <div className="export-orientation-btns">
                  <button
                    className={`export-orientation-btn ${orientation === 'landscape' ? 'active' : ''}`}
                    onClick={() => setOrientation('landscape')}
                    disabled={isExporting}
                    title={t.modals.export.landscape}
                  >
                    <svg width="24" height="16" viewBox="0 0 24 16" fill="none" stroke="currentColor" strokeWidth="1.5">
                      <rect x="1" y="1" width="22" height="14" rx="2" />
                    </svg>
                  </button>
                  <button
                    className={`export-orientation-btn ${orientation === 'portrait' ? 'active' : ''}`}
                    onClick={() => setOrientation('portrait')}
                    disabled={isExporting}
                    title={t.modals.export.portrait}
                  >
                    <svg width="16" height="24" viewBox="0 0 16 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                      <rect x="1" y="1" width="14" height="22" rx="2" />
                    </svg>
                  </button>
                </div>
              </div>

              <div className="export-section export-section-inline">
                <label className="export-section-label">{t.modals.export.margin}</label>
                <div className="export-margin-wrapper">
                  <input
                    type="range"
                    min="0"
                    max="30"
                    value={margin}
                    onChange={(e) => setMargin(parseInt(e.target.value))}
                    className="export-range"
                    disabled={isExporting}
                  />
                  <span className="export-range-value">{margin}mm</span>
                </div>
              </div>
            </div>
          )}

          {/* JPG 전용 옵션 */}
          {format === 'jpg' && (
            <div className="export-options-group">
              <div className="export-section export-section-inline">
                <label className="export-section-label">{t.modals.export.quality}</label>
                <select
                  value={quality}
                  onChange={(e) => setQuality(e.target.value as ImageQuality)}
                  className="export-select"
                  disabled={isExporting}
                >
                  <option value="low">{t.modals.export.qualityLow}</option>
                  <option value="medium">{t.modals.export.qualityMedium}</option>
                  <option value="high">{t.modals.export.qualityHigh}</option>
                </select>
              </div>

              <div className="export-section export-section-inline">
                <label className="export-section-label">{t.modals.export.resolution}</label>
                <div className="export-scale-btns">
                  <button
                    className={`export-scale-btn ${scale === 1 ? 'active' : ''}`}
                    onClick={() => setScale(1)}
                    disabled={isExporting}
                  >
                    1x
                  </button>
                  <button
                    className={`export-scale-btn ${scale === 2 ? 'active' : ''}`}
                    onClick={() => setScale(2)}
                    disabled={isExporting}
                  >
                    2x
                  </button>
                  <button
                    className={`export-scale-btn ${scale === 3 ? 'active' : ''}`}
                    onClick={() => setScale(3)}
                    disabled={isExporting}
                  >
                    3x
                  </button>
                </div>
              </div>

              <div className="export-section export-section-inline">
                <label className="export-section-label">{t.modals.export.backgroundColor}</label>
                <div className="export-color-wrapper">
                  <input
                    type="color"
                    value={backgroundColor}
                    onChange={(e) => setBackgroundColor(e.target.value)}
                    className="export-color-input"
                    disabled={isExporting}
                  />
                  <span className="export-color-value">{backgroundColor}</span>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* 진행 상태 표시 */}
        {progress && isExporting && (
          <div className="export-progress">
            <div className="export-progress-bar-wrapper">
              <div
                className="export-progress-bar"
                style={{ width: `${progress.progress}%` }}
              />
            </div>
            <span className="export-progress-text">{progress.message}</span>
          </div>
        )}

        {/* 완료/에러 메시지 */}
        {progress && progress.status === 'complete' && (
          <div className="export-success">
            <svg width="20" height="20" viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M16 6l-8 8-4-4" />
            </svg>
            <span>{t.modals.export.exportComplete}</span>
          </div>
        )}

        {progress && progress.status === 'error' && (
          <div className="export-error">
            <svg width="20" height="20" viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="2">
              <circle cx="10" cy="10" r="8" />
              <path d="M10 6v4M10 14h.01" />
            </svg>
            <span>{progress.message}</span>
          </div>
        )}

        {/* 푸터 */}
        <div className="export-modal-footer">
          <button
            className="export-btn export-btn-secondary"
            onClick={onClose}
            disabled={isExporting}
          >
            {t.common.cancel}
          </button>
          <button
            className="export-btn export-btn-primary"
            onClick={handleExport}
            disabled={isExporting || !filename.trim()}
          >
            {isExporting ? t.modals.export.exporting : (format === 'pdf' ? t.modals.export.exportPdf : t.modals.export.exportJpg)}
          </button>
        </div>
      </div>
    </div>
  );
};

export default ExportModal;
